/**
 * The deep-link grammar shared by Hub web and the native clients — ONE table.
 *
 * Three consumers read this file, and they must agree or the feature is worse
 * than not having it:
 *
 *   1. **Hub web** decides whether the page you are looking at has a native
 *      twin, and offers "Open in app"
 *      (`apps/hub/components/mobile/open-in-app-banner.tsx`).
 *   2. **Hub's `apple-app-site-association`** publishes the exact path list iOS
 *      is allowed to hand to the app
 *      (`apps/hub/app/.well-known/apple-app-site-association/route.ts`).
 *   3. **The iOS app** parses the incoming URL — generated into
 *      `HubDeepLinks.swift` by `scripts/gen-swift-hub-contracts.mjs`, the same
 *      way the route table is.
 *
 * If those three drift, the symptom is a universal link that opens the app to
 * the wrong screen, or a banner offering a jump to a screen that does not exist.
 * Hand-mirroring is how that happens, so nothing here is hand-mirrored.
 *
 * ## A deep link is untrusted input
 *
 * Any web page, email, or app can send `wtchub://open?...`, and iOS hands a
 * universal link to the app before the user is even signed in. So the grammar is
 * CLOSED: a link resolves only if its path matches an entry in this table
 * exactly, and an id-bearing entry additionally requires a real UUID. Anything
 * else is dropped — no navigation, no fetch. Mirrors the desktop guard in
 * `apps/desktop/src/shared/deep-link-contract.ts`.
 */
/**
 * The id placeholder — the same literal `route-manifest.ts` exports as `ID`.
 *
 * It is redeclared here rather than imported because this module is loaded by
 * `scripts/gen-swift-hub-contracts.mjs` through `node --experimental-strip-types`,
 * whose ESM resolver cannot follow the package's extensionless specifiers. A
 * TYPE import (above) is erased before Node ever sees it; a value import is not.
 * The generator asserts `ID === DEEP_LINK_ID` and fails the build if these two
 * ever disagree, so the duplication cannot silently drift.
 */
export const DEEP_LINK_ID = "{id}";
/** URL scheme both native clients register. Shared with `apps/desktop`. */
export const HUB_APP_SCHEME = "wtchub";
/**
 * The app's five tabs, as a link vocabulary.
 *
 * Deliberately a public, stable string set rather than the client's internal
 * enum: a widget already on someone's home screen holds one of these strings,
 * so renaming a tab internally must not break it.
 */
export const HUB_APP_TABS = ["today", "sales", "meridian", "search", "more"];
/**
 * Screens a link may open that are neither a tab root nor a single record —
 * the app's own navigation destinations.
 */
export const HUB_APP_SCREENS = [
    "pipeline",
    "contactList",
    "companyList",
    "ticketQueue",
    "agenda",
    "mail",
    "timeSheet",
    "sequenceQueue",
    "documentScan",
    "offerings",
    /** The signed-in user's OWN digital business card — QR, share, edit. */
    "card",
    /** A client's shared support mailbox, picked by company. */
    "supportInbox",
    /** The social composer + what is already queued to go out. */
    "social",
];
/**
 * Modal FORMS a native client can raise, as opposed to screens it can navigate
 * to.
 *
 * This vocabulary exists because of what Hub's `client-redirect` actions
 * actually do: eight of them call `hooks.openModal(...)` — they open a form on
 * the page you are already on, with the resolved record filled in. The native
 * equivalent is a sheet, not a push, and a client that treated them as
 * navigation would land the user on a screen with no form on it.
 *
 * Kept separate from `HUB_APP_SCREENS` deliberately: a screen is a place (it
 * goes on the navigation stack, Back returns from it) and a sheet is a task (it
 * is modal, it is dismissed, it does not belong in history). Merging the two
 * lists would make an action's declaration ambiguous about which one it wants.
 *
 * NOT a deep-link target: a link that pops a half-finished form on launch is
 * hostile, so these are reachable only from an action a user tapped.
 */
export const HUB_APP_SHEETS = [
    /** Log a call / email / meeting / note against a contact. */
    "logActivity",
    /** Create a task, optionally linked to the resolved record. */
    "newTask",
    /** Create a deal for the resolved company/contact. */
    "newDeal",
    /** Book a meeting. */
    "newMeeting",
    /** Raise a support ticket for the resolved contact. */
    "newTicket",
];
/**
 * Every Hub web page with a native twin.
 *
 * ORDER MATTERS in one direction only: a more specific path must precede a
 * prefix of itself, so `/tickets/{id}` wins over `/tickets`. The matcher does
 * not re-sort; keep new entries in the same shape.
 *
 * Paths were verified against `apps/hub/app/**` page files. Notably contacts,
 * companies and deals live at the ROOT (`/contacts/[id]`), not under `/crm/` —
 * the older `HUB_WEB_PATH_MANIFEST` said `/crm/contacts/{id}`, which 404s.
 */
export const HUB_DEEP_LINK_ROUTES = [
    // Records.
    { key: "contact", web: `/contacts/${DEEP_LINK_ID}`, target: { kind: "entity", entity: "contacts" } },
    { key: "company", web: `/companies/${DEEP_LINK_ID}`, target: { kind: "entity", entity: "companies" } },
    { key: "deal", web: `/deals/${DEEP_LINK_ID}`, target: { kind: "entity", entity: "deals" } },
    { key: "ticket", web: `/tickets/${DEEP_LINK_ID}`, target: { kind: "entity", entity: "tickets" } },
    { key: "task", web: `/tasks/${DEEP_LINK_ID}`, target: { kind: "entity", entity: "tasks" } },
    {
        key: "communication",
        web: `/communications/${DEEP_LINK_ID}`,
        target: { kind: "entity", entity: "communications" },
    },
    // Lists and work surfaces.
    { key: "myTickets", web: "/tickets/my-tickets", target: { kind: "screen", screen: "ticketQueue" } },
    {
        key: "myEntries",
        web: "/time-tracking/my-entries",
        target: { kind: "screen", screen: "timeSheet" },
    },
    { key: "today", web: "/today", target: { kind: "tab", tab: "today" } },
    { key: "work", web: "/work", target: { kind: "tab", tab: "today" } },
    { key: "chat", web: "/chat", target: { kind: "tab", tab: "meridian" } },
    { key: "search", web: "/search", target: { kind: "tab", tab: "search" } },
    { key: "pipeline", web: "/deals", target: { kind: "screen", screen: "pipeline" } },
    { key: "contactList", web: "/contacts", target: { kind: "screen", screen: "contactList" } },
    { key: "companyList", web: "/companies", target: { kind: "screen", screen: "companyList" } },
    { key: "ticketQueue", web: "/tickets", target: { kind: "screen", screen: "ticketQueue" } },
    { key: "agenda", web: "/crm/calendar", target: { kind: "screen", screen: "agenda" } },
    { key: "mail", web: "/crm/inbox", target: { kind: "screen", screen: "mail" } },
    { key: "timeSheet", web: "/time-tracking", target: { kind: "screen", screen: "timeSheet" } },
    { key: "sequenceQueue", web: "/sequences", target: { kind: "screen", screen: "sequenceQueue" } },
    { key: "documentScan", web: "/tools/scan", target: { kind: "screen", screen: "documentScan" } },
    { key: "offerings", web: "/offerings", target: { kind: "screen", screen: "offerings" } },
    {
        key: "supportInbox",
        web: "/work/support-inbox",
        target: { kind: "screen", screen: "supportInbox" },
    },
    {
        key: "socialPosts",
        web: "/social-media-posts",
        target: { kind: "screen", screen: "social" },
    },
    // `/account` is the whole account hub on the web (profile, security, passkeys,
    // card). The phone has native screens for the others already, and the card is
    // the only one worth a link, so an /account link lands there rather than
    // nowhere. Deliberately last: it is the least specific of the three.
    { key: "card", web: "/account", target: { kind: "screen", screen: "card" } },
];
/**
 * Paths that must NEVER open in the app, expressed for the AASA `components`
 * exclusion list.
 *
 * `/s/*` is the secure-link surface: those URLs go to EXTERNAL parties
 * (`SECURE_LINKS`), and handing one to an employee-only app breaks the
 * recipient's flow, not ours. `/auth/*` and `/oauth/*` are browser handshakes —
 * intercepting one strands a sign-in. `/api/*` and `/.well-known/*` are not
 * pages at all.
 *
 * `/card/*` joins them for the same reason as `/s/*`: a published digital
 * business card is handed to CLIENTS and strangers. If the app claimed that
 * path, a rep with the app installed would tap their own shared card and get an
 * employee screen instead of what the recipient sees — and worse, the link's
 * whole job is to work for someone who will never install this app.
 */
export const HUB_DEEP_LINK_EXCLUSIONS = [
    "/api/*",
    "/auth/*",
    "/oauth/*",
    "/s/*",
    "/card/*",
    "/.well-known/*",
];
const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
/** Split a path into segments, ignoring empty ones (`//a/` -> `["a"]`). */
function segments(path) {
    return path.split("/").filter((segment) => segment.length > 0);
}
/**
 * Match a Hub web pathname against the table.
 *
 * Query string and hash are ignored — pass `new URL(href).pathname`. A locale
 * prefix is NOT stripped: Hub does not localize these routes, and quietly
 * accepting `/fr/contacts/…` would widen the grammar for no benefit.
 */
export function matchHubWebPath(pathname) {
    const actual = segments(pathname);
    for (const route of HUB_DEEP_LINK_ROUTES) {
        const pattern = segments(route.web);
        if (pattern.length !== actual.length)
            continue;
        let id;
        let matched = true;
        for (let index = 0; index < pattern.length; index += 1) {
            const expected = pattern[index];
            const found = actual[index];
            if (expected === DEEP_LINK_ID) {
                // The id segment is the only place untrusted text enters. UUID or
                // nothing — a Hub entity id is always a UUID, so anything else is
                // either a typo or someone probing.
                if (!UUID_PATTERN.test(found)) {
                    matched = false;
                    break;
                }
                id = found;
                continue;
            }
            if (expected.toLowerCase() !== found.toLowerCase()) {
                matched = false;
                break;
            }
        }
        if (matched) {
            return id === undefined
                ? { key: route.key, target: route.target }
                : { key: route.key, target: route.target, id };
        }
    }
    return null;
}
/**
 * Build the `wtchub://` URL for a target.
 *
 * The app accepts three query forms and nothing else:
 * `?type=<entity>&id=<uuid>`, `?tab=<tab>`, `?screen=<screen>`.
 */
export function hubAppURL(target, id) {
    const base = `${HUB_APP_SCHEME}://open`;
    switch (target.kind) {
        case "entity": {
            if (id === undefined || !UUID_PATTERN.test(id)) {
                throw new Error("hubAppURL: an entity target requires a UUID id");
            }
            return `${base}?type=${encodeURIComponent(target.entity)}&id=${encodeURIComponent(id)}`;
        }
        case "tab":
            return `${base}?tab=${encodeURIComponent(target.tab)}`;
        case "screen":
            return `${base}?screen=${encodeURIComponent(target.screen)}`;
    }
}
/**
 * The whole web→app hop in one call: a Hub pathname in, a `wtchub://` URL out,
 * or `null` when this page has no native twin (so the caller renders nothing).
 */
export function hubAppLinkForWebPath(pathname) {
    const match = matchHubWebPath(pathname);
    if (match === null)
        return null;
    return hubAppURL(match.target, match.id);
}
/**
 * The `components` array for the AASA `applinks` detail — exclusions FIRST
 * (Apple evaluates in order and the first match wins), then one entry per
 * linkable path. `${DEEP_LINK_ID}` becomes `*`, which is as narrow as AASA gets; the app
 * still enforces the UUID.
 */
export function appleAppSiteAssociationComponents() {
    const excluded = HUB_DEEP_LINK_EXCLUSIONS.map((path) => ({
        "/": path,
        exclude: true,
        comment: "Not a native surface — must stay in the browser.",
    }));
    const included = HUB_DEEP_LINK_ROUTES.map((route) => ({
        "/": route.web.split(DEEP_LINK_ID).join("*"),
        comment: route.key,
    }));
    return [...excluded, ...included];
}
